"use client";

import React, { useState, useEffect } from "react";
import { Cpu, Wrench, Flame, RotateCw, Users as Users2, Trophy, ArrowRight, Compass, Sparkles, CircleCheck as CheckCircle2, CircleQuestionMark as HelpCircle, GraduationCap } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function WhoItsForStartingPoint() {
  const [lang, setLang] = useState<"vi" | "en">("vi");
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll<HTMLElement>({
    threshold: 0.15
  });
  useEffect(() => {
    const savedLang = localStorage.getItem("roboto_lang") as "vi" | "en" | null;
    if (savedLang === "vi" || savedLang === "en") {
      setLang(savedLang);
    }
    const handleLangChange = (e: Event) => {
      const customEv = e as CustomEvent<string>;
      if (customEv.detail === "vi" || customEv.detail === "en") {
        setLang(customEv.detail);
      } else {
        const current = localStorage.getItem("roboto_lang") as "vi" | "en" | null;
        if (current === "vi" || current === "en") {
          setLang(current);
        }
      }
    };
    window.addEventListener("roboto-lang-change", handleLangChange);
    return () => {
      window.removeEventListener("roboto-lang-change", handleLangChange);
    };
  }, []);
  const part1Content = {
    badge: {
      vi: "Định Hướng Học Tập",
      en: "Learning Direction"
    },
    title: {
      vi: "Ai phù hợp với hướng thi đấu?",
      en: "Who is the competition pathway for?"
    },
    subtitle: {
      vi: "Lộ trình thi đấu dành cho mọi học sinh có mong muốn thử sức và nâng cao năng lực. Chúng tôi tập trung vào sự chủ động, tính kiên trì và niềm yêu thích công nghệ thay vì tạo áp lực tuyển chọn khắt khe.",
      en: "The competition pathway is designed for any student looking to challenge themselves and advance their skills. We focus on initiative, perseverance, and passion for technology rather than strict selective barriers."
    },
    criteria: [{
      icon: Cpu,
      title: {
        vi: "Yêu thích Robotics & Công nghệ",
        en: "Enjoys Robotics & Technology"
      },
      desc: {
        vi: "Thích tò mò về cơ chế vận hành của cỗ máy, mạch điện và cách lập trình cho robot chuyển động.",
        en: "Curious about mechanical systems, circuitry, and logic programming for dynamic robots."
      }
    }, {
      icon: Wrench,
      title: {
        vi: "Thích giải quyết thử thách kỹ thuật",
        en: "Enjoys Technical Challenges"
      },
      desc: {
        vi: "Hào hứng khi đối mặt với các bài toán cấu trúc cơ khí, cảm biến và thuật toán cần cải tiến.",
        en: "Excited to solve complex structural, sensor, and algorithm engineering problems."
      }
    }, {
      icon: Flame,
      title: {
        vi: "Muốn thử sức bài toán nâng cao",
        en: "Seeks Demanding Problems"
      },
      desc: {
        vi: "Sẵn sàng vượt ra khỏi các bài học cơ bản để đối mặt với đề thi theo chuẩn thi đấu quốc tế.",
        en: "Ready to go beyond basics and take on real-world competitive challenge tasks."
      }
    }, {
      icon: RotateCw,
      title: {
        vi: "Kiên trì thử nghiệm & sửa lỗi",
        en: "Comfortable Debugging & Testing"
      },
      desc: {
        vi: "Không ngại thất bại khi mô hình chưa hoạt động, sẵn sàng tinh chỉnh lại thuật toán nhiều lần.",
        en: "Resilient when prototypes fail, willing to iterate and refine code continuously."
      }
    }, {
      icon: Users2,
      title: {
        vi: "Yêu thích làm việc nhóm",
        en: "Thrives in Teamwork"
      },
      desc: {
        vi: "Học cách phân công vai trò, trao đổi chiến thuật và hỗ trợ đồng đội trong không khí thi đấu sôi nổi.",
        en: "Learns role division, tactical planning, and mutual support under dynamic conditions."
      }
    }, {
      icon: Trophy,
      title: {
        vi: "Khám phá cơ hội thi đấu",
        en: "Explores Competition Pathways"
      },
      desc: {
        vi: "Mong muốn giao lưu, cọ xát tại các giải đấuRobotics trong nước và quốc tế để tích lũy trải nghiệm.",
        en: "Eager to represent teams, compete locally & globally, and build valuable portfolio milestones."
      }
    }]
  };
  const part2Content = {
    badge: {
      vi: "Lựa Chọn Lộ Trình",
      en: "Decision Guide"
    },
    title: {
      vi: "Con nên bắt đầu từ đâu?",
      en: "Where should your child start?"
    },
    subtitle: {
      vi: "Dựa vào kinh nghiệm học tập thực tế của con để chọn bước đi phù hợp nhất.",
      en: "Choose the best starting path based on your child's current robotics experience."
    },
    paths: [{
      id: "A",
      label: {
        vi: "Tùy chọn A",
        en: "Option A"
      },
      title: {
        vi: "Chưa từng học Robotics",
        en: "Never studied Robotics"
      },
      desc: {
        vi: "Bắt đầu với chương trình khám phá nền tảng STEM, tư duy lập trình căn bản và lắp ráp mô hình chuẩn tiêu chuẩn.",
        en: "Start with foundational STEM concepts, basic algorithm logic, and hands-on LEGO robotics building."
      },
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/path_option_a_stem_basics.png",
        srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/path_option_a_stem_basics.png-wvc-srcset",
        sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/path_option_a_stem_basics.png-wvc-sizes",
        alt: "Foundational STEM and LEGO robotics for beginners"
      },
      cta: {
        vi: "Xem Khung Chương Trình",
        en: "View Curriculum"
      },
      link: "/LearningPathCurriculumPage",
      highlight: false
    }, {
      id: "B",
      label: {
        vi: "Tùy chọn B",
        en: "Option B"
      },
      title: {
        vi: "Đã có nền tảng Robotics",
        en: "Already has a Robotics foundation"
      },
      desc: {
        vi: "Cần đánh giá kỹ năng hiện tại để xếp lớp chuyên sâu phù hợp hoặc tham gia tư vấn định hướng cùng chuyên gia.",
        en: "Requires skill assessment to place into the right level or join personalized consultation with mentors."
      },
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/path_option_b_skills_assessment.webp",
        srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/path_option_b_skills_assessment.webp-wvc-srcset",
        sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/path_option_b_skills_assessment.webp-wvc-sizes",
        alt: "Student robotics skills evaluation and mentoring"
      },
      cta: {
        vi: "Đăng Ký Tư Vấn & Đánh Giá",
        en: "Book Free Assessment"
      },
      link: "/FreeTrialContactPage",
      highlight: false
    }, {
      id: "C",
      label: {
        vi: "Tùy chọn C",
        en: "Option C"
      },
      title: {
        vi: "Đã học Robotics & muốn thi đấu",
        en: "Has studied Robotics & wants to compete"
      },
      desc: {
        vi: "Gia nhập ngay Hệ sinh thái thi đấu Robotics để chuẩn bị cho các giải VEX, FIRST LEGO League, WRO hoặc Robofest.",
        en: "Join our Competition Ecosystem to prepare for VEX, FIRST LEGO League, WRO, or Robofest arenas."
      },
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/path_option_c_competition_arena.png",
        srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/path_option_c_competition_arena.png-wvc-srcset",
        sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/path_option_c_competition_arena.png-wvc-sizes",
        alt: "Robotics competition team preparing for arena tournament"
      },
      cta: {
        vi: "Khám Phá Hệ Sinh Thái Thi Đấu",
        en: "Explore Competition Ecosystem"
      },
      link: "#competition-ecosystem",
      highlight: true
    }, {
      id: "D",
      label: {
        vi: "Tùy chọn D",
        en: "Option D"
      },
      title: {
        vi: "Đã học hoàn thành HP6",
        en: "Has completed HP6"
      },
      desc: {
        vi: "Chuyển tiếp lên lộ trình AI & Robotics cao cấp. Lưu ý: Việc hoàn thành HP6 giúp tiếp thu tốt hơn chứ không mặc định bắt buộc thi đấu.",
        en: "Seamlessly transition to Advanced AI & Robotics. Note: HP6 completion enhances readiness without making competition mandatory."
      },
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/path_option_d_advanced_ai_robotics.png",
        srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/path_option_d_advanced_ai_robotics.png-wvc-srcset",
        sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/path_option_d_advanced_ai_robotics.png-wvc-sizes",
        alt: "Advanced AI and hardware engineering track"
      },
      cta: {
        vi: "Xem Lộ Trình AI & Robotics",
        en: "View AI & Robotics Pathway"
      },
      link: "/RoboticsLearningPathwayPage#enjoy-ai-pathway",
      highlight: false
    }]
  };
  return <section ref={sectionRef} id="whoitsforstartingpoint" data-nav="light" className="relative bg-background text-foreground py-16 md:py-24 overflow-hidden isolate">
      {/* Background Subtle Tech Grid Motif */}
      <div className="aria-hidden:true pointer-events-none absolute inset-0 -z-10 opacity-35" style={{
      backgroundImage: `radial-gradient(var(--border) 1px, transparent 1px)`,
      backgroundSize: "24px 24px"
    }} />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* PART 1: TARGET AUDIENCE ALIGNMENT */}
        <div className={`transition-all duration-700 ease-out transform ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          {/* Header Part 1 */}
          <div className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
            <Badge variant="secondary" className="mb-3 px-3 py-1 text-xs font-semibold uppercase tracking-wider">
              <Sparkles className="size-3.5 mr-1" />
              {part1Content.badge[lang]}
            </Badge>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight text-foreground mb-4 font-serif">
              {part1Content.title[lang]}
            </h2>
            <p className="text-muted-foreground text-base sm:text-lg leading-relaxed">
              {part1Content.subtitle[lang]}
            </p>
          </div>

          {/* Criteria Cards Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-20 md:mb-28">
            {part1Content.criteria.map((item, index) => {
            const IconComp = item.icon;
            return <Card key={index} data-index={index} className="group relative border border-border/80 bg-card/80 backdrop-blur-xs shadow-sm hover:shadow-md hover:-translate-y-1 transition-all duration-300 rounded-xl overflow-hidden flex flex-col justify-between">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between mb-3">
                      <div className="p-2.5 rounded-lg bg-secondary/15 text-secondary border border-secondary/20 group-hover:bg-secondary group-hover:text-secondary-foreground transition-colors duration-300">
                        <IconComp className="size-5" />
                      </div>
                      <span className="inline-flex items-center text-xs font-mono font-medium text-secondary bg-secondary/10 px-2 py-0.5 rounded-lg border border-secondary/20">
                        <CheckCircle2 className="size-3 mr-1" />
                        Fit #{index + 1}
                      </span>
                    </div>
                    <CardTitle className="text-lg font-semibold text-foreground group-hover:text-primary transition-colors">
                      {item.title[lang]}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-muted-foreground text-sm leading-relaxed">
                      {item.desc[lang]}
                    </CardDescription>
                  </CardContent>
                </Card>;
          })}
          </div>
        </div>

        {/* Separator Accent Line */}
        <div className="relative my-12 md:my-16 flex items-center justify-center">
          <div className="absolute inset-0 flex items-center aria-hidden:true">
            <div className="w-full border-t border-border" />
          </div>
          <div className="relative bg-background px-4 text-xs font-mono text-muted-foreground uppercase tracking-widest flex items-center gap-2">
            <Compass className="size-4 text-primary" />
            <span>ROBOTO Decision Pathway</span>
          </div>
        </div>

        {/* PART 2: 4-PATH DECISION GUIDE */}
        <div className={`transition-all duration-700 delay-200 ease-out transform ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          {/* Header Part 2 */}
          <div className="text-center max-w-3xl mx-auto mb-12">
            <Badge variant="outline" className="mb-3 px-3 py-1 text-xs font-semibold uppercase tracking-wider border-primary/30 text-primary bg-primary/5">
              <GraduationCap className="size-3.5 mr-1" />
              {part2Content.badge[lang]}
            </Badge>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight text-foreground mb-4 font-serif">
              {part2Content.title[lang]}
            </h2>
            <p className="text-muted-foreground text-base sm:text-lg leading-relaxed">
              {part2Content.subtitle[lang]}
            </p>
          </div>

          {/* 4 Pathway Decision Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
            {part2Content.paths.map((pathItem, idx) => <Card key={pathItem.id} data-index={idx} className={`group relative border flex flex-col justify-between rounded-xl overflow-hidden transition-all duration-300 hover:-translate-y-1 shadow-sm hover:shadow-md ${pathItem.highlight ? "border-primary/50 bg-card shadow-primary/5 ring-1 ring-primary/20" : "border-border bg-card"}`}>
                <div>
                  {/* Technical Branding Image Banner */}
                  <div className="relative h-40 w-full overflow-hidden border-b border-border/60 bg-slate-950">
                    <img src={pathItem.image.src} data-wvc-srcset={pathItem.image.srcset} data-wvc-sizes={pathItem.image.sizes} alt={pathItem.image.alt} loading="lazy" className="w-full h-full object-cover opacity-85 group-hover:scale-105 transition-transform duration-500" />
                    <div className="absolute inset-0 bg-gradient-to-t from-card via-slate-900/30 to-transparent" />
                  </div>

                  <CardHeader className="pb-3 pt-4 sm:pt-5">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-mono font-semibold uppercase px-2.5 py-1 rounded-md bg-muted text-muted-foreground border border-border">
                        {pathItem.label[lang]}
                      </span>
                      {pathItem.highlight && <span className="text-xs font-medium bg-primary text-primary-foreground px-2.5 py-0.5 rounded-lg flex items-center gap-1 shadow-xs">
                          <Trophy className="size-3" />
                          Recommended for Competition
                        </span>}
                    </div>
                    <CardTitle className="text-xl font-bold text-foreground pt-1">
                      {pathItem.title[lang]}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pb-6">
                    <p className="text-muted-foreground text-sm leading-relaxed">
                      {pathItem.desc[lang]}
                    </p>
                  </CardContent>
                </div>

                <CardFooter className="pt-0 border-t border-border/40 mt-auto pt-4">
                  <Button asChild variant={pathItem.highlight ? "default" : "outline"} className="w-full group/btn justify-between">
                    <Link to={pathItem.link}>
                      <span>{pathItem.cta[lang]}</span>
                      <ArrowRight className="size-4 group-hover/btn:translate-x-1 transition-transform" />
                    </Link>
                  </Button>
                </CardFooter>
              </Card>)}
          </div>

          {/* Footer Guidance Note */}
          <div className="mt-12 bg-muted/50 border border-border rounded-xl p-4 sm:p-6 text-center max-w-3xl mx-auto flex flex-col sm:flex-row items-center gap-3 text-sm text-muted-foreground">
            <HelpCircle className="size-5 text-primary shrink-0" />
            <p className="text-left leading-relaxed">
              {lang === "vi" ? "Cần trao đổi thêm để lựa chọn đúng? Hãy đăng ký buổi kiểm tra năng lực kỹ thuật miễn phí cùng huấn luyện viên ROBOTO." : "Unsure which starting path fits best? Book a complimentary tech assessment session with a ROBOTO lead coach."}
            </p>
            <Button asChild size="sm" variant="ghost" className="shrink-0 text-primary font-medium hover:text-primary">
              <Link to="/FreeTrialContactPage">
                {lang === "vi" ? "Liên hệ ngay" : "Contact us"}
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}